import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaFileInvoiceDollar, FaLayerGroup, FaList, FaPen, FaPlus, FaSearch, FaUserTie } from 'react-icons/fa';
import { ImCancelCircle } from 'react-icons/im';
import ErpTemplate from '../../components/ErpTemplate';
import Alert from '../../components/Alert';
import api from '../../services/api';
import '../../css/financeiro-parcela-pagar.css';

export default function PainelHotel() {
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    const [filtro, setFiltro] = useState({ nome: '', buscando: false });
    const [hoteis, setHoteis] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const controllerRef = useRef(null);

    const podeEditar = !['evento'].includes(perfil);
    const podeExcluir = ['admin', 'diretor', 'gerenteprojeto', 'gerentefinanceiro'].includes(perfil);

    const buscar = () => {
        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        setFiltro((state) => ({ ...state, buscando: true }));
        api({
            method: 'get',
            action: '/participante',
            token,
            params: {
                ...filtro,
                tipoCadastro: ['hotel'],
            },
            signal: controllerRef.current.signal
        })
            .then((response) => {
                setHoteis(response.data.data ?? []);
                setFiltro((state) => ({ ...state, buscando: false }));
                setSelectedIds(new Set());
                setPage(1);
            })
            .catch((err) => {
                if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return;
                setFiltro((state) => ({ ...state, buscando: false }));
                setSelectedIds(new Set());
                setPage(1);
            });
    };

    const excluir = (id) => {
        Alert({
            icon: 'error',
            confirmButton: 'Sair',
            denyButton: <><ImCancelCircle size={16} /> Excluir</>,
            title: 'Excluir hotel',
            callBack: (result) => {
                if (!result.isDenied) {
                    return;
                }
                api({
                    method: 'delete',
                    action: `/participante/${id}`,
                    token,
                }).then(() => {
                    buscar();
                }).catch(() => { });
            }
        });
    };

    useEffect(() => {
        document.querySelector('#nome')?.focus();
        buscar();
        return () => { if (controllerRef.current) controllerRef.current.abort(); };
        // eslint-disable-next-line
    }, []);

    const paginados = useMemo(() => {
        const inicio = (page - 1) * pageSize;
        return hoteis.slice(inicio, inicio + pageSize);
    }, [hoteis, page, pageSize]);

    const totalPaginas = Math.max(1, Math.ceil(hoteis.length / pageSize));
    const allSelected = paginados.length > 0 && paginados.every((row) => selectedIds.has(row.id));
    const selectedArr = Array.from(selectedIds);

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
            return;
        }
        setSelectedIds(new Set(paginados.map((row) => row.id)));
    };

    const formatDocumento = (row) => {
        if (row.cnpj && row.cpf) {
            return `${row.cnpj} / ${row.cpf}`;
        }
        return row.cnpj || row.cpf || '';
    };

    const formatMunicipio = (row) => row.enderecomunicipio ? `${row.enderecomunicipio.nome}-${row.enderecomunicipio.uf}` : '';

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Hotéis</h1>
                        <p className="fin-pp__subtitle">Subfluxo do hub de fornecedores com o mesmo padrão visual do financeiro.</p>
                    </div>
                    <div className="fin-pp__actions">
                        {podeEditar && (
                            <Link to="/cadastroHotel" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill">
                                <FaPlus size={14} /> Novo Hotel
                            </Link>
                        )}
                        <Link to="/fornecedor" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaUserTie size={14} /> Fornecedores
                        </Link>
                        <Link to="/freelancer" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaUserTie size={14} /> Freelancers
                        </Link>
                        <Link to="/fornecedorGrupo" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaLayerGroup size={14} /> Grupos
                        </Link>
                        <Link to="/participanteCategoria" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaList size={14} /> Categorias
                        </Link>
                        <Link to="/compra" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaFileInvoiceDollar size={14} /> Faturas
                        </Link>
                    </div>
                </div>

                <form
                    className="fin-pp__card fin-pp__search"
                    style={{ marginBottom: 16, padding: '12px 16px' }}
                    onSubmit={(e) => { buscar(); e.preventDefault(); }}
                >
                    <div className="fin-pp__search-field">
                        <FaSearch className="fin-pp__search-ic" size={16} />
                        <input
                            id="nome"
                            className="fin-pp__input"
                            type="text"
                            placeholder="Buscar pelo nome do hotel"
                            value={filtro.nome ?? ''}
                            onChange={(e) => setFiltro((state) => ({ ...state, nome: e.target.value }))}
                        />
                    </div>
                    <button type="submit" className="fin-pp__btn fin-pp__btn--primary" disabled={filtro.buscando}>
                        {filtro.buscando ? 'Buscando...' : 'Buscar'}
                    </button>
                </form>

                {selectedArr.length === 1 && podeExcluir && (
                    <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="button" className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--sm" onClick={() => excluir(selectedArr[0])}>
                            <ImCancelCircle size={14} /> Excluir
                        </button>
                    </div>
                )}

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 980, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 44 }}>
                                        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                                    </th>
                                    <th style={{ width: 62 }}></th>
                                    <th style={{ width: 90 }}>Situação</th>
                                    <th style={{ width: 90 }}>Código</th>
                                    <th>Nome</th>
                                    <th>Município</th>
                                    <th>CNPJ/CPF</th>
                                    <th>Nome Fantasia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtro.buscando ? (
                                    <tr><td colSpan={8} className="fin-pp__empty"><span className="fin-pp__loading"><span className="fin-pp__spin"></span>Buscando...</span></td></tr>
                                ) : paginados.length === 0 ? (
                                    <tr><td colSpan={8} className="fin-pp__empty">Nenhum registro</td></tr>
                                ) : paginados.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(row.id)}
                                                onChange={() => toggleSelect(row.id)}
                                            />
                                        </td>
                                        <td>
                                            {podeEditar ? (
                                                <Link to={`/cadastroHotel/${row.id}`} className="fin-pp__rowbtn" title="Alterar hotel">
                                                    <FaPen size={14} />
                                                </Link>
                                            ) : null}
                                        </td>
                                        <td>
                                            <span className={`fin-pp__pill ${row.situacaoRegistro === 'inativo' ? 'is-muted' : 'is-ok'}`}>
                                                {row.situacaoRegistro ?? 'ativo'}
                                            </span>
                                        </td>
                                        <td className="fin-pp__cell-muted">{row.codigo ?? ''}</td>
                                        <td className="fin-pp__cell-strong">{row.nome ?? ''}</td>
                                        <td className="fin-pp__cell-muted">{formatMunicipio(row)}</td>
                                        <td className="fin-pp__cell-muted">{formatDocumento(row)}</td>
                                        <td className="fin-pp__cell-muted">{row.nomefantasia ?? ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">{hoteis.length} registros</div>
                        <div className="fin-pp__pagination-ctrls">
                            <select
                                className="fin-pp__select"
                                style={{ height: 34 }}
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={50}>50 / pág</option>
                            </select>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page <= 1} onClick={() => setPage((state) => state - 1)}>
                                Anterior
                            </button>
                            <span className="fin-pp__pagination-info">{page} / {totalPaginas}</span>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page >= totalPaginas} onClick={() => setPage((state) => state + 1)}>
                                Próxima
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </ErpTemplate>
    );
}
